import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  RefreshCw,
} from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { useMultiStrategyScreen, useTradeIdeaEditionToday } from "@/domains/trade-ideas/hooks/useTradeIdeas";
import type {
  EnergyScoreSnapshot,
  MacroEventContext,
  MarketRegimeSnapshot,
  MultiStrategyBestIdea,
  MultiStrategyBookSummary,
  MultiStrategyScreenEntry,
  MultiStrategyScreenRow,
} from "@/domains/trade-ideas/lib/contracts";
import {
  buildDeskIdeaWatchInitialState,
  DIRECTION_UNAVAILABLE_REASON,
  deskIdeaWatchAvailability,
  hasKnownDirection,
  useDeskIdeaStalenessInvalidation,
} from "@/domains/trade-ideas/lib/deskIdeaWatchPrefill";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/shared/lib/queryKeys";
import { BE_ADR_TONE_CLASS, LIQUIDITY_TONE_CLASS, VOL_REGIME_TONE_CLASS } from "@/domains/trade-ideas/lib/deskThresholdPresentation";
import { buildTickerContextLink } from "@/domains/trade-ideas/lib/tickerContextLink";
import { AskClaudeButton } from "@/domains/trade-ideas/components/AskClaudeButton";
import { IdeaDeepDiveSections } from "@/domains/trade-ideas/components/ThesisQuestionSections";
import {
  archetypeLabel,
  beAdrRowView,
  computeStaleness,
  deriveWatchOutCodes,
  directionView,
  displayEntryResolution,
  energyRatingValue,
  energySignalCoverageLabel,
  energySignalLabel,
  expiryWithDteLabel,
  formatMaxLossPerContract,
  formatImpliedProbabilityPct,
  formatPerShareUsd,
  formatSpreadPct,
  IMPLIED_PROBABILITY_TOOLTIP,
  isEntryPriced,
  macroEventMastheadLine,
  marketRegimeMastheadLine,
  optionsFlowView,
  overlayString,
  parseDecimalString,
  parseGeneratedAt,
  premiumSide,
  projectedDisplayExpiry,
  projectedDisplayStrikes,
  reasonCodeLabel,
  regimeCopy,
  relativeStrengthView,
  runningRecordFacts,
  spreadLiquiditySignifier,
  summarizeStaleness,
  volRegimeTone,
  type OverlayView,
  type RunningRecordFacts,
  type StalenessSummary,
  type StalenessTier,
} from "@/domains/trade-ideas/lib/screenerLabels";
import { DeskRulesStrip } from "@/domains/trade-ideas/components/DeskRulesStrip";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";
import type { ApiFn } from "@/shared/lib/types";
import { cn } from "@/shared/lib/utils";

// Internal / shadow-research view over the multi-strategy screener collection
// (trade_idea_multi_strategy_screen). Rows are NOT customer-authoritative:
// customer-eligible (debit) ideas render first; any row whose top expression is
// shadow (e.g. credit) drops into a distinct, shaded "internal only" band with
// no trade CTA. Colors map to the portal's semantic theme tokens (gain / loss /
// warning / info / muted) so the table reads correctly in light and dark.

// ---------------------------------------------------------------------------
// Small parsing / overlay helpers
// ---------------------------------------------------------------------------

function parseFit(value: string | null | undefined): number {
  if (value == null) return NaN;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : NaN;
}

// Best-idea (Phase 4) signal accessors. The signal score is the
// composite quality_score in [0, 1]; prefer the top-level best_idea_score, then
// the nested breakdown's quality_score. It is a RATING, not a probability — the
// UI renders it as a 0-100 number with NO "%".
function bestIdea(row: MultiStrategyScreenRow): MultiStrategyBestIdea | null {
  return (row.best_idea ?? null) as MultiStrategyBestIdea | null;
}

// JS-1091 round 2 (FIX 5): an expression-specific claim (axes/confidence in
// the drawer, the Ask-Claude prompt's signal score) must be sourced from the
// SELECTED expression's OWN score breakdown -- server-merged onto the entry
// as `expression_best_idea` (services/apis/domains/trade_ideas/screener_reads
// .py's `_merge_expression_ids`, the SAME shape `bestIdea(row)` already
// reads) -- never the ROW-level `best_idea`, which is keyed to the row's
// PRIMARY expression and would silently misattribute a claim when a
// non-primary alternative is being shown. `null` (never falls back to the
// row-level score) when this entry carries no independent breakdown --
// FIX 5's "omit rather than substitute" rule for legacy data.
function expressionBestIdea(entry: MultiStrategyScreenEntry): MultiStrategyBestIdea | null {
  return (entry.expression_best_idea ?? null) as MultiStrategyBestIdea | null;
}

// Raw signal in [0, 1] (NaN when absent). Used only for sorting so rows
// without a score sink to the bottom.
function signalUnit(row: MultiStrategyScreenRow): number {
  // Guard null/empty BEFORE Number(): Number(null) === 0 (finite), which would
  // fabricate a signal of 0 for a row that has NO score and float it above
  // genuinely-absent rows instead of sinking it. An absent score must be NaN.
  const raw = row.best_idea_score;
  if (raw != null && raw !== "") {
    const primary = Number(raw);
    if (Number.isFinite(primary)) return primary;
  }
  const nestedRaw = bestIdea(row)?.quality_score;
  if (nestedRaw != null && nestedRaw !== "") {
    const nested = Number(nestedRaw);
    if (Number.isFinite(nested)) return nested;
  }
  return NaN;
}

// Signal as a rounded 0-100 rating, or null when unavailable.
function signalScoreValue(row: MultiStrategyScreenRow): number | null {
  const unit = signalUnit(row);
  if (!Number.isFinite(unit)) return null;
  return Math.round(Math.max(0, Math.min(1, unit)) * 100);
}

function topEntry(row: MultiStrategyScreenRow): MultiStrategyScreenEntry | undefined {
  // Rows arrive fit-sorted, but re-sort defensively so the hero entry is truly
  // the highest-fit expression rather than an assumed ordering.
  return [...row.entries].sort((a, b) => {
    const fa = parseFit(a.fit_score);
    const fb = parseFit(b.fit_score);
    if (Number.isNaN(fa) && Number.isNaN(fb)) return 0;
    if (Number.isNaN(fa)) return 1;
    if (Number.isNaN(fb)) return -1;
    return fb - fa;
  })[0];
}

function isShadowEntry(entry: MultiStrategyScreenEntry | undefined): boolean {
  if (!entry) return false;
  if (entry.shadow === true) return true;
  if (entry.customer_eligible === false) return true;
  return entry.authority_label === "SHADOW_RESEARCH";
}

function assetTag(row: MultiStrategyScreenRow): string | null {
  // asset_type lives on the passthrough `listing` object (untyped by the schema).
  const listing = (row as Record<string, unknown>).listing;
  const assetType =
    listing && typeof listing === "object"
      ? (listing as Record<string, unknown>).asset_type
      : undefined;
  if (assetType === "ETF") return "ETF";
  if (assetType === "COMMON_STOCK") return "Stock";
  return null;
}

// generated_at is a passthrough field (not on the typed schema), read defensively.
function rowGeneratedAt(row: MultiStrategyScreenRow): string | null {
  const raw = (row as Record<string, unknown>).generated_at;
  return typeof raw === "string" ? raw : null;
}

function newestGeneratedAt(rows: MultiStrategyScreenRow[]): Date | null {
  let newest: number | null = null;
  for (const row of rows) {
    const raw = rowGeneratedAt(row);
    if (raw === null) continue;
    const ms = Date.parse(raw);
    if (Number.isNaN(ms)) continue;
    if (newest === null || ms > newest) newest = ms;
  }
  return newest === null ? null : new Date(newest);
}

function relativeUpdated(date: Date | null): string | null {
  if (!date) return null;
  const diffMs = Date.now() - date.getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

// JS-1087 (fix 1): a null/unrecognized `direction` (the wire contract's
// `direction` is `z.string().nullish()`, so this is a real, expected input,
// not a defensive-only edge case) must never render as a confident
// "BEARISH" -- see screenerLabels.directionView's own docstring for the
// full honesty rationale. Kept as a thin row-level wrapper (rather than
// inlining `directionView(row.direction)` at every call site) so a future
// caller cannot regress back to the old `=== "BULLISH" ? ... : bearish`
// two-way branch by accident.
function rowDirectionView(row: MultiStrategyScreenRow) {
  return directionView(row.direction);
}

// ---------------------------------------------------------------------------
// Presentational atoms
// ---------------------------------------------------------------------------

// Signal: a 0-100 RATING (never a "%") with a thin proportional meter.
// Higher = our signals agree more strongly across setup / confirmation / risk.
function SignalMeter({ score }: { score: number | null }) {
  const valid = score != null;
  const clamped = valid ? Math.max(0, Math.min(100, score)) : 0;
  return (
    <div className="flex items-center justify-end gap-[7px]">
      <span
        className={cn(
          "min-w-[18px] font-mono text-[12.5px] font-bold tabular-nums",
          valid ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {valid ? clamped : "—"}
      </span>
      <div className="h-[5px] w-10 overflow-hidden rounded-[3px] bg-muted" aria-hidden>
        <span
          className="block h-full rounded-full bg-primary"
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}

// JS-1088 (round 2 FIX 4): a compact "Ctx" flag -- Rel. Strength and Options
// Flow each collapse to ONE short glyph + label pair here (was a full
// OverlayCell in its own column), with the fuller label/detail available on
// hover via `title`, so two overlays fit side-by-side in a single column
// without losing the underlying fact. ✓ confirm (green) / ✗ conflict
// (amber) / neutral (muted) -- conflict is amber, deliberately distinct
// from bearish red, per the design's color semantics. a11y: the accessible
// name (via `title`, which browsers/AT expose on hover AND on focus for a
// keyboard/screen-reader user) always carries the full label + detail, so
// nothing is lost to the abbreviation -- color is never the only signal
// (the glyph text itself, e.g. "✓ RS", is part of the visible content).
function CompactOverlayFlag({ prefix, view }: { prefix: string; view: OverlayView }) {
  if (!view.available || !view.label) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
        <span aria-hidden>·</span>
        {prefix}
      </span>
    );
  }
  const toneClass =
    view.tone === "confirm" ? "text-gain" : view.tone === "conflict" ? "text-warning" : "text-muted-foreground";
  const glyph = view.tone === "confirm" ? "✓" : view.tone === "conflict" ? "✗" : "·";
  const fullTitle = [view.label, view.detail].filter(Boolean).join(" ");
  return (
    <span
      className={cn("inline-flex items-center gap-1 text-[11px] font-medium", toneClass)}
      title={`${prefix}: ${fullTitle}`}
    >
      <span aria-hidden className="font-semibold">{glyph}</span>
      {prefix}
    </span>
  );
}

function SortCaret({ active, direction }: { active: boolean; direction: "asc" | "desc" }) {
  if (!active) return <ChevronsUpDown className="size-3 opacity-40" aria-hidden />;
  return direction === "desc" ? (
    <ChevronDown className="size-3" aria-hidden />
  ) : (
    <ChevronRight className="size-3 rotate-90" aria-hidden />
  );
}

function LegendDot({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn("size-2 rounded-full", className)} aria-hidden />
      <span>{label}</span>
    </span>
  );
}

// JS-1086 (hi-fi Desk) + review fix (HIGH): freshness chip, derived purely
// client-side from generated_at. A single tone map shared by both the
// table-summary badge (worst tier across every row -- see
// summarizeStaleness's own docstring for why "newest row" was an honesty
// bug) and the per-row OFFENDER badge (a row whose OWN tier is worse than
// fresh still carries its badge, so a stale/unknown-age straggler is never
// concealed behind a calm summary line -- only fresh rows stay unbadged,
// preserving the column-budget intent). Stale/unknown are the tiers meant to
// draw the eye (amber/warning); fresh is intentionally quiet.
const STALENESS_TONE: Record<StalenessTier, string> = {
  fresh: "border-gain/25 bg-gain/10 text-gain",
  aging: "border-border bg-surface text-muted-foreground",
  stale: "border-warning/30 bg-warning/10 text-warning",
  unknown: "border-border bg-surface text-muted-foreground",
};

// Per-row freshness badge -- mounted ONLY on rows whose own tier is worse
// than "fresh" (see ScreenRow below), so a fresh list stays visually quiet
// and only genuine offenders are marked.
function StalenessBadge({ generatedAt }: { generatedAt: string | null }) {
  const staleness = computeStaleness(generatedAt);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className={cn("cursor-default", STALENESS_TONE[staleness.tier])}
          aria-label={`Freshness: ${staleness.label}`}
        >
          {staleness.label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{staleness.tooltip}</TooltipContent>
    </Tooltip>
  );
}

// JS-1091 round 3 (FIX 1): visible, explicit signal that the row's
// server-stamped primary_expression_id matched no entry -- a genuine data
// inconsistency (see displayEntryResolution), never silently hidden by
// falling back to a guessed entry. "Re-run pending" is honest, not a
// promise: this Portal has no way to trigger or know about a re-sweep: it
// states what WOULD resolve the inconsistency (the row is normally
// refreshed automatically by the next scheduled sweep) without claiming
// pending status information this component doesn't have.
function InconsistentExpressionBadge() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className="cursor-default border-warning/30 bg-warning/10 text-warning"
          aria-label="Expression data inconsistent — re-run pending"
        >
          Data inconsistent
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        This row&apos;s selected expression could not be resolved from its own data — expression data
        inconsistent, re-run pending. Economics for this row are honestly unavailable until the next sweep.
      </TooltipContent>
    </Tooltip>
  );
}

// Table-summary freshness badge -- reflects the WORST tier across every row
// on the page (summarizeStaleness), never the newest row's. Renders nothing
// for an empty row set (nothing to summarize).
function StalenessSummaryBadge({ summary }: { summary: StalenessSummary | null }) {
  if (!summary) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className={cn("cursor-default", STALENESS_TONE[summary.tier])}
          aria-label={`Freshness: ${summary.label}`}
        >
          {summary.label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{summary.tooltip}</TooltipContent>
    </Tooltip>
  );
}

// JS-1066: a small badge on rows whose discovery_lane is "ENERGY" -- CORE
// rows (discovery_lane absent, or any value other than "ENERGY") get NO
// badge at all (absence = default, keeps the table quiet). The composite
// renders as a bare 0-100 rating with NO "%" (mirrors the Signal meter's
// rating-not-probability framing exactly -- ADR-009 / ADR-011); the tooltip
// lists every contributing sub-signal's own 0-100 value plus the honest
// "n/6 signals available" coverage line.
function EnergyBadge({ snapshot }: { snapshot: EnergyScoreSnapshot | null | undefined }) {
  if (!snapshot) return null;
  const composite = energyRatingValue(snapshot.composite);
  const coverage = energySignalCoverageLabel(snapshot.signals_available);
  const contributing = Object.entries(snapshot.signals ?? {})
    .map(([name, result]) => ({ name, value: energyRatingValue(result?.score) }))
    .filter((entry): entry is { name: string; value: number } => entry.value !== null);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className="cursor-default border-warning/30 bg-warning/10 text-warning"
          aria-label={`Energy rating: ${composite ?? "unavailable"}`}
        >
          Energy {composite ?? "—"}
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-[240px]">
        <div className="space-y-1">
          {contributing.length > 0 ? (
            <ul className="space-y-0.5">
              {contributing.map((entry) => (
                <li key={entry.name} className="flex items-center justify-between gap-3">
                  <span>{energySignalLabel(entry.name)}</span>
                  <span className="font-mono tabular-nums">{entry.value}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p>No contributing sub-signals available.</p>
          )}
          {coverage && <p className="text-muted-foreground">{coverage}</p>}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Overlay derivations (RS / Flow / Events)
//
// JS-1089 round 2 (FIX 2): relativeStrengthView/optionsFlowView (and the
// OverlayView type they return) now live in screenerLabels.ts so the daily
// edition's card expansion can derive the SAME adverse-signal watch-outs
// from its own frozen row (NO-TECH-DEBT: one implementation).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Expanded thesis drawer
// ---------------------------------------------------------------------------
//
// The drawer's per-question primitives live in ThesisQuestionSections.tsx so
// this screen and the daily edition share one implementation.

// ---------------------------------------------------------------------------
// JS-1088 no-repetition audit (JS-1084 contract test (ii): "the drawer...
// repeats nothing from the row. If a fact is already legible on the row, the
// drawer must not restate it.") Every fact promoted to the row in this
// ticket, and its drawer disposition:
//
//   Fact               | Row (JS-1088)         | Drawer (post-JS-1088)
//   -------------------|------------------------|---------------------------
//   Executable fill    | Fill column            | REMOVED (was "Entry price"
//                       |                        | in ExecutionFrictionRows)
//   Mid price          | Entry column           | REMOVED (was part of the
//                       |                        | same "mid $x · fill $y")
//   Expiry + DTE       | Idea line/drawer head  | REMOVED (was a bare
//                       |                        | "Expiry" EconomicsRow)
//   Spread % of premium| Sprd% column           | REMOVED (was "Spread /
//                       |                        | premium" in
//                       |                        | ExecutionFrictionRows)
//   Breakeven distance | BE×ADR column          | N/A -- be_adr_ratio never
//                       | (be_adr_ratio)         | existed in the drawer;
//                       |                        | the drawer's own
//                       |                        | "Breakeven" EconomicsRow
//                       |                        | (the raw strike-price
//                       |                        | level(s)) is a DIFFERENT
//                       |                        | fact from the ratio and
//                       |                        | is kept (never shown on
//                       |                        | the row).
//   Max loss           | Max loss column        | KEPT -- owner-signed
//                       |                        | exception (JS-1084
//                       |                        | commission notes:
//                       |                        | "max-loss row+drawer dual
//                       |                        | placement ALLOWED --
//                       |                        | argument anchor != a
//                       |                        | restatement"). Q3 uses it
//                       |                        | as the "what if wrong"
//                       |                        | argument's anchor number,
//                       |                        | not a bare re-display.
//   Net debit/credit    | Fill column            | REMOVED (round 2 FIX 3:
//   per share            | (the SAME               | was "Net debit / sh"
//                       | executable per-share    | EconomicsRow -- the SAME
//                       | price, projected from   | per-share fact, under a
//                       | priced_legs for a       | label that was actively
//                       | credit -- FIX 2)        | WRONG for a credit entry,
//                       |                        | whose natural_entry_per_
//                       |                        | share is a net CREDIT).
//
// Round 2 FIX 2 note: "Strikes" (kept, unaffected by the audit above) is now
// PROJECTED from priced_legs when canonical strikes is empty
// (projectedDisplayStrikes) -- the same fix that lets the ROW's Expiry
// column see a real, priced credit's tenor now also lets the DRAWER's own
// Strikes line see its strikes, rather than both silently showing nothing
// for every credit idea.
//
// Everything else already in the drawer (max profit, raw breakeven price
// levels, provenance tags, structure-vs-alternative note, OI
// wall context, exit rule) has no row counterpart and is unaffected by this
// audit.
// ---------------------------------------------------------------------------

function CollapseEvaluationButton({ onCollapse }: { onCollapse: () => void }) {
  return (
    <button
      type="button"
      onClick={onCollapse}
      aria-label="Collapse idea evaluation"
      aria-expanded="true"
      title="Collapse evaluation"
      className="inline-flex size-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-hover hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ChevronRight className="size-4" aria-hidden />
    </button>
  );
}

function drawerExpiryLabel(expiry: string | null | undefined): string | null {
  if (!expiry) return null;
  const parsed = new Date(`${expiry}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function ThesisDrawer({
  row,
  top,
  onCollapse,
}: {
  row: MultiStrategyScreenRow;
  top: MultiStrategyScreenEntry;
  onCollapse: () => void;
}) {
  const navigate = useNavigate();

  // JS-1091: Q1 ("Why now") + activation/invalidation/horizon read ONLY from
  // the projected setup block when the row is BOUND to a real, current
  // SetupSnapshot (see domains.trade_ideas.desk_idea_argument -- the setups
  // pipeline's own formal thesis, never synthesized from an option's price,
  // a strategy archetype's reason codes, or the SMA screener's direction).
  // A row with no `setup_binding` at all (a legacy row, produced before this
  // ticket shipped) or an explicit `"UNBOUND"` status falls back to the
  // PRE-EXISTING entry-derived facts -- but those are now explicitly labeled
  // as screen-level evidence, never presented as a setup thesis, per the
  // house honest-absence rule.
  const isSetupBound = row.setup_binding?.status === "BOUND" && row.setup != null;
  const setup = isSetupBound ? row.setup : undefined;

  const whyCodes = setup ? [] : (top.reason_codes ?? []);
  // JS-1071: the economics panel shows the best-PRICED entry, not necessarily
  // `top` (the highest-fit entry) -- under the failsafe fit ranking (active
  // while iv_rank is None) the almost-always-unpriced vertical sorts first,
  // so a sibling entry that actually priced was being hidden behind a
  // hardcoded "pending" message. `top` still owns the row's identity (why
  // this / alternatives / fit score / shadow classification); only the
  // economics section below switches to the displayed entry, and is labeled
  // when the two differ so nothing is misattributed.
  //
  // JS-1091 round 3 (FIX 1): a present-but-unmatched primary_expression_id
  // stamp is resolved BEFORE falling back to `top` -- an inconsistent
  // resolution renders a distinct, minimal drawer body (below) instead of
  // ever substituting `top` as if it were the real selected expression.
  const displayResolution = displayEntryResolution(row);
  const isInconsistent = displayResolution.status === "inconsistent";
  const display = displayResolution.status === "ok" ? displayResolution.entry : top;
  const headerIsPriced = !isInconsistent && isEntryPriced(display);
  const abstain = display.abstain_reason_codes ?? [];
  const abstainHeadline = abstain.length > 0 ? reasonCodeLabel(abstain[0]) : reasonCodeLabel("DEBIT_ECONOMICS_NO_ELIGIBLE_EXPRESSION");

  // JS-1091 round 2 (FIX 5): the Signal-breakdown panel is a claim about
  // `display` -- the SELECTED expression -- so its axes/confidence come
  // from THAT expression's own score breakdown, never the row-level one
  // (which is keyed to the PRIMARY expression and could silently
  // misattribute the breakdown when `display` is a non-primary
  // alternative). Absent (a legacy row with no per-expression score at
  // all) means the panel simply does not render (see `axes.length > 0`
  // below) -- omitted, never substituted with the row's own score.
  const idea = expressionBestIdea(display);
  const drawerDirection = rowDirectionView(row);
  const drawerDirectionClass =
    drawerDirection.kind === "BULLISH"
      ? "text-gain"
      : drawerDirection.kind === "BEARISH"
        ? "text-loss"
        : "text-muted-foreground";
  const drawerSignal = signalScoreValue(row);
  const drawerExpiry = headerIsPriced ? drawerExpiryLabel(projectedDisplayExpiry(display)) : null;
  const drawerStrikes = headerIsPriced ? projectedDisplayStrikes(display) : [];
  const drawerStructure = [archetypeLabel(display.archetype), drawerStrikes.length > 0 ? drawerStrikes.map((strike) => Number(strike).toLocaleString("en-US", { maximumFractionDigits: 2 })).join("/") : null]
    .filter((value): value is string => Boolean(value) && value !== "—")
    .join(" ");
  const drawerSide = premiumSide(display.archetype);
  const drawerFit = parseFit(display.fit_score);
  const drawerHeaderLabel = [
    row.ticker ?? "—",
    `${drawerDirection.kind === "BULLISH" ? "▲" : drawerDirection.kind === "BEARISH" ? "▼" : "◆"} ${drawerDirection.label}`,
    drawerStructure || null,
    drawerExpiry ? `· ${drawerExpiry}` : null,
    `${drawerSignal ?? "—"} SIGNAL`,
  ].filter((value): value is string => Boolean(value)).join(" ");
  const policyParts = [
    display.customer_eligible === true ? "Customer eligible" : isShadowEntry(display) ? "Research only" : null,
    drawerSide,
    Number.isFinite(drawerFit) ? `fit ${drawerFit.toFixed(2)}` : null,
    row.discovery_lane ? `${row.discovery_lane.replace(/_/g, " ").toLowerCase()} lane` : null,
  ].filter((value): value is string => Boolean(value));

  // JS-1081: "+Watch" pre-fill -- honest-absence disabled reason when the
  // display entry cannot support it, defense-in-depth null-check at click
  // time even though the button is already disabled in that case.
  //
  // JS-1087 (fix 1): a "+Watch" pre-fill encodes a directional position, so
  // it must fail closed when the row's own direction is unrecognized -- the
  // direction check is applied FIRST (before the entry-pricing checks) since
  // an unknown direction is the more fundamental reason (see
  // deskIdeaWatchPrefill.hasKnownDirection's own docstring).
  const knownDirection = hasKnownDirection(row);
  const drawerGeneratedAt = rowGeneratedAt(row);
  const queryClient = useQueryClient();
  useDeskIdeaStalenessInvalidation(drawerGeneratedAt, () =>
    queryClient.invalidateQueries({ queryKey: [...queryKeys.tradeIdeasRoot(), "screener", "list"] }),
  );
  const watchAvailability = knownDirection
    ? deskIdeaWatchAvailability(display, drawerGeneratedAt)
    : { available: false, reason: DIRECTION_UNAVAILABLE_REASON };
  const openPrefilledWatch = () => {
    if (!knownDirection) return;
    const initialState = buildDeskIdeaWatchInitialState(row, display);
    if (!initialState) {
      // JS-1094 (demo blocker FIX 3): the button is only ever enabled when
      // `watchAvailability.available` is true, but that check and this
      // builder call are two SEPARATE evaluations of overlapping-but-not-
      // identical logic -- a race (the underlying data refetching between
      // render and click) or a decode-drift edge case could still make the
      // builder decline here even though the button rendered enabled. A
      // silent no-op (the pre-fix behavior) leaves the visitor thinking the
      // click did nothing, with no explanation. Surface it honestly instead.
      toast.error("This idea can't be pre-filled into a watch right now — try refreshing.");
      return;
    }
    navigate("/portal/watches/new", { state: { watchWizardInitialState: initialState } });
  };

  // JS-1082: "Open <ticker> ticker view" -- a SHAREABLE deep link (query
  // params, never router state -- see tickerContextLink.ts's module
  // docstring for why) carrying this idea's ledger_id so the ticker
  // dashboard can resolve and overlay its context. `null` (never a guessed
  // link) when the display entry has no confirmed `ledger_id`, exactly like
  // the ledger-id gate `buildDeskIdeaWatchInitialState` already applies to
  // its own provenance field.
  //
  // JS-1087 (adversarial-review fix): also gated on `knownDirection` -- an
  // unknown-direction idea previously escaped the fail-closed gate applied
  // to "+Watch"/"Ask Claude" by leaving THIS link enabled, and the ticker
  // dashboard destination (TickerDashboard.tsx) maps a non-BULLISH/BEARISH
  // direction to an invisible empty-string state. Disabled with the SAME
  // honest DIRECTION_UNAVAILABLE_REASON as the other two direction-dependent
  // actions, never a silent escape hatch.
  const tickerContextHref = display.ledger_id && knownDirection
    ? buildTickerContextLink({
        ticker: row.ticker ?? "",
        ledgerId: display.ledger_id,
        origin: "screener",
        rowId: row.row_id,
      })
    : null;

  // JS-1091 round 3 (FIX 1): an INCONSISTENT resolution renders a distinct,
  // minimal drawer body instead of the full Q1-Q4 argument frame -- every
  // action below (Ask Claude, +Watch, ticker view) is expression-specific
  // and disabled honestly, never left clickable against a guessed entry.
  if (isInconsistent) {
    return (
      <div data-testid="desk-drawer" className="flex h-full min-h-0 flex-col bg-surface">
        <header className="flex shrink-0 items-center justify-between border-b border-border px-3 py-2">
          <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Evaluation</span>
          <CollapseEvaluationButton onCollapse={onCollapse} />
        </header>
        <div className="px-4 py-4">
          <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 p-3 text-[12px] text-warning">
            <span aria-hidden>⚠</span>
            <p>
              Expression data inconsistent — re-run pending. This row&apos;s server-selected expression could not
              be matched against its own entries, so no economics, thesis detail, or action can be shown for it
              honestly. It should resolve on the next scheduled sweep.
            </p>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled title="Unavailable — expression data inconsistent">
              + Watch
            </Button>
            <Button size="sm" variant="outline" disabled title="Unavailable — expression data inconsistent">
              Open ticker view
            </Button>
            <AskClaudeButton
              ledgerId={null}
              promptInput={{
                ticker: row.ticker ?? "",
                entry: { archetype: null, entry_type: null, natural_entry_per_share: null, strikes: [], expiry: null, expression_best_idea: null },
                direction: row.direction,
              }}
              disabledReason="Unavailable — expression data inconsistent"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="desk-drawer" className="flex h-full min-h-0 flex-col bg-surface">
      <header className="shrink-0 border-b border-border px-[18px] py-3">
        <div aria-label={drawerHeaderLabel} className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <span className="text-[19px] font-bold tracking-[-0.01em] text-foreground">{row.ticker ?? "—"}</span>
          <span className={cn("font-mono text-[11.5px] font-semibold", drawerDirectionClass)}>
            <span aria-hidden>{drawerDirection.kind === "BULLISH" ? "▲" : drawerDirection.kind === "BEARISH" ? "▼" : "◆"}</span>{" "}
            {drawerDirection.label}
          </span>
          {drawerStructure && <span className="font-mono text-[12px] text-muted-foreground">{drawerStructure}</span>}
          {drawerExpiry && <span className="font-mono text-[12px] text-muted-foreground">· {drawerExpiry}</span>}
          <span className="ml-auto font-mono text-[20px] font-bold tabular-nums text-foreground">
            {drawerSignal ?? "—"}{" "}<span className="text-[9px] font-normal uppercase tracking-[0.08em] text-muted-foreground">signal</span>
          </span>
          <CollapseEvaluationButton onCollapse={onCollapse} />
        </div>
        {policyParts.length > 0 && (
          <p className="mt-1 font-mono text-[9.5px] text-muted-foreground">Policy · {policyParts.join(" · ")}</p>
        )}
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-3.5">
      {/* JS-1104: the flat card register is shared with the edition and
          ticker surfaces; this persistent rail only supplies its selected
          expression and row-scoped evidence. */}
      <IdeaDeepDiveSections
        entry={display}
        row={row}
        reasonCodes={whyCodes}
        setup={setup}
        // DESK-009 (JS-1144): `display` is the SELECTED expression, so its
        // own row-face disclosures ride the watch-outs -- a disclosure is a
        // caveat on the idea actually being shown, not a row-wide signal.
        watchOutCodes={deriveWatchOutCodes(row, display)}
        abstainHeadline={abstainHeadline}
        idea={idea}
      />
      </div>

      <div className="grid shrink-0 grid-cols-3 gap-2 border-t border-border px-[18px] py-3">

        {/* JS-1087 (fix 3): the "View strike setup →" button that used to
            render here had NO click handler -- dead chrome. Its obvious
            intent (jump to this idea's strike/setup context) is already
            served by the "Open <ticker> ticker view" button immediately
            below, which deep-links to the SAME ticker dashboard with this
            idea's ledger_id bound in (tickerContextLink.ts) -- the natural
            strike-setup anchor already present in this panel. Rather than
            wire a second, redundant control to the identical destination
            (or invent a new one with no backing surface), the dead button
            is removed outright: no dead chrome, no duplicate affordance. */}

        {/* JS-1081 (adversarial-review FIX 3): the shadow/non-customer-
            eligible gate lives INSIDE deskIdeaWatchAvailability now (the one
            shared authority boundary both Desk-idea surfaces call through),
            not as a local `!shadow` wrapper here -- a shadow entry renders
            this button DISABLED with an honest reason, never silently
            hidden. */}
        <Button
          size="sm"
          variant="outline"
          className="w-full bg-foreground text-background hover:bg-foreground/90"
          disabled={!watchAvailability.available}
          title={watchAvailability.reason ?? undefined}
          onClick={openPrefilledWatch}
        >
          + Watch
        </Button>

        {/* JS-1082: "Open <ticker> ticker view" -- disabled honestly (never
            hidden) when this idea has no confirmed ledger_id yet, OR (JS-1087
            adversarial-review fix) when the row's own direction is
            unrecognized -- the two are distinct honest reasons, so the
            direction reason is checked first (the more fundamental one,
            matching "+Watch"/"Ask Claude"'s own ordering). */}
        <Button
          asChild={Boolean(tickerContextHref)}
          size="sm"
          variant="outline"
          className="w-full"
          disabled={!tickerContextHref}
          title={
            tickerContextHref
              ? undefined
              : !knownDirection
                ? DIRECTION_UNAVAILABLE_REASON
                : "This idea has no ledger reference yet."
          }
        >
          {tickerContextHref ? (
            <Link to={tickerContextHref}>Open {row.ticker ?? "ticker"} ticker view</Link>
          ) : (
            <span>Open ticker view</span>
          )}
        </Button>

        {/* JS-1083: "Ask Claude" -- copies a facts-only prompt naming this
            idea's ledger_id, disabled honestly (never hidden) when the
            display entry has no confirmed ledger_id yet. JS-1091 round 2
            (FIX 5): the prompt's signal-score fact is now sourced from
            `display.expression_best_idea` (this SAME selected entry's own
            score), never the row -- see askClaudePrompt.ts's
            signalScoreLabel for why. */}
        <AskClaudeButton
          ledgerId={display.ledger_id}
          promptInput={{ ticker: row.ticker ?? "", entry: display, direction: row.direction }}
          disabledReason={knownDirection ? null : DIRECTION_UNAVAILABLE_REASON}
          className="w-full [&_button]:border-primary [&_button]:text-primary"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function ScreenRow({
  row,
  top,
  expanded,
  onToggle,
}: {
  row: MultiStrategyScreenRow;
  top: MultiStrategyScreenEntry | undefined;
  expanded: boolean;
  onToggle: () => void;
}) {
  const direction = rowDirectionView(row);
  const directionClass =
    direction.kind === "BULLISH" ? "text-gain" : direction.kind === "BEARISH" ? "text-loss" : "text-muted-foreground";
  const railClass =
    direction.kind === "BULLISH" ? "bg-gain" : direction.kind === "BEARISH" ? "bg-loss" : "bg-muted-foreground/50";
  const regime = regimeCopy(row.iv_rv_regime);
  const rs = relativeStrengthView(row);
  const flow = optionsFlowView(row);
  const signal = signalScoreValue(row);

  const earnings = row.earnings as Record<string, unknown> | null | undefined;
  const withinVeto = earnings?.within_veto_window === true;
  const daysToEarnings = earnings?.days_to_earnings;
  const nextEarnings = overlayString(earnings, "next_earnings_date");

  // JS-1071: the row's headline structure label reflects the best-PRICED
  // entry (displayEntryForRow), not `top` (highest-fit) -- a row whose priced
  // expression is a long call should read "Long Call", not the unpriced
  // vertical's "Bull Call Spread". `top` still drives shadow classification /
  // direction / premium-side, matching this component's existing "top is row
  // identity" convention (see ThesisDrawer).
  //
  // JS-1091 round 3 (FIX 1): a present-but-unmatched primary_expression_id
  // stamp is a genuine data inconsistency, not a legacy gap -- rendering
  // `top` as if it were the resolved display entry (round 2's `?? top`)
  // silently guessed a possibly-WRONG expression. This row now renders an
  // explicit "expression data inconsistent" badge and honestly dashes every
  // expression-specific cell instead.
  const displayResolution = displayEntryResolution(row);
  const isInconsistent = displayResolution.status === "inconsistent";
  const display = displayResolution.status === "ok" ? displayResolution.entry : undefined;
  const side = premiumSide(display?.archetype);

  // JS-1086 review fix (HIGH): an OFFENDER row (its own tier worse than
  // "fresh" -- aging, stale, or unknown-age) keeps a visible per-row badge
  // even though the table-summary line already reports the worst tier
  // page-wide. Without this, a single stale/unknown row sitting among many
  // fresh ones would be indistinguishable from any other row once the
  // per-row badge moved to the summary line. A fresh row stays unbadged
  // (absence = default), preserving the column-budget intent that motivated
  // moving freshness off the row in the first place.
  const ownGeneratedAt = rowGeneratedAt(row);
  const ownStaleness = computeStaleness(ownGeneratedAt);
  const isStalenessOffender = ownStaleness.tier !== "fresh";

  // JS-1088: the executable decision facts -- fill, expiry/DTE, max loss,
  // BE×ADR (or cushion, or condor near-wing cushion), spread% -- all read
  // off `display` (the best-PRICED entry, the SAME entry the row's
  // structure label already reflects), never `top`. Every one of these is
  // honestly absent (dashes, matching every other unpriced-row cell on this
  // table) when `display` has no real priced legs -- see
  // displayEntryForRow's own "falls back to entries[0]" note: an entirely-
  // unpriced row's `display` still has no execution_friction/expiry/
  // maximum_loss_usd, so every cell below renders "—" naturally without a
  // separate priced-check gate.
  //
  // JS-1088 (round 2 FIX 2): expiry/strikes are PROJECTED from priced_legs
  // when the entry's own canonical fields are empty (the credit-entry gap
  // -- see projectedDisplayExpiry/projectedDisplayStrikes's own docstrings)
  // so a real, priced credit's tenor is never blank just because only the
  // debit pipeline populates the canonical `expiry` field.
  const fillLabel = formatPerShareUsd(display?.execution_friction?.net_realistic_fill_per_share);
  const midLabel = formatPerShareUsd(display?.execution_friction?.net_mid_per_share);
  const displayExpiry = display ? projectedDisplayExpiry(display) : null;
  const expiryDteLabel = expiryWithDteLabel(displayExpiry);
  const maxLossLabel = formatMaxLossPerContract(display?.maximum_loss_usd);
  // JS-1088 (round 2 FIX 1): the row reads the ALREADY-CLASSIFIED
  // be_adr_ratio envelope (payoff_class stamped by the backend kernel that
  // computed it) -- never re-derives DEBIT_DIRECTIONAL/ONE_SIDED_CREDIT
  // from the row's own archetype here. beAdrRowView is the ONE place the
  // class -> tone/word/qualifier dispatch happens.
  const beAdrView = beAdrRowView(display?.be_adr_ratio);
  const spreadPctLabel = formatSpreadPct(display?.execution_friction?.spread_width_pct_of_premium);
  const spreadSig = spreadLiquiditySignifier(display?.execution_friction?.spread_width_pct_of_premium);
  const popLabel = formatImpliedProbabilityPct(display?.implied_probability?.probability_of_profit);
  const isDisplayPriced = Boolean(display && isEntryPriced(display));
  const strikes = display ? projectedDisplayStrikes(display) : [];
  const expiryShort = expiryDteLabel?.split(" · ")[0] ?? null;
  const structureParts = [archetypeLabel(display?.archetype), strikes.length > 0 ? strikes.join("/") : null, expiryShort]
    .filter((value): value is string => Boolean(value) && value !== "—");
  const abstainReason = display?.abstain_reason_codes?.[0];
  const structureLine = isDisplayPriced
    ? structureParts.join(" · ")
    : reasonCodeLabel(abstainReason ?? "DEBIT_ECONOMICS_NO_ELIGIBLE_EXPRESSION");
  const entryLabel = midLabel || fillLabel
    ? `${side === "sell premium" ? "cr " : ""}${midLabel ?? "—"} · ${fillLabel ?? "—"}`
    : null;
  const rawBeAdr = parseDecimalString(display?.be_adr_ratio?.ratio);
  const beAdrLabel = rawBeAdr === null ? null : `${rawBeAdr.toFixed(1)}x`;

  // JS-1086 (hi-fi Desk) a11y: the row is a real keyboard target -- a tab
  // stop with a visible focus ring, opened/closed by Enter or Space, not
  // pointer-only. Deliberately NOT `role="button"` -- that would replace the
  // <tr>'s implicit "row" role, breaking every `getByRole("row"/"button")`
  // query this table's own tests (and screen readers navigating the table
  // structurally) already rely on; `tabIndex` + `onKeyDown` alone is enough
  // to make the row keyboard-operable without changing its semantic role.
  const onRowKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onToggle();
  };

  const cell = "whitespace-nowrap px-2.5 py-[7px] font-mono text-[12px] tabular-nums";
  return (
    <tr
      onClick={onToggle}
      onKeyDown={onRowKeyDown}
      tabIndex={0}
      className={cn(
        "group cursor-pointer border-b border-border align-middle transition-colors hover:bg-hover",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset motion-reduce:transition-none",
        isShadowEntry(top) && "bg-foreground/[0.015]",
        expanded && "bg-primary/[0.14] shadow-[inset_3px_0_0_var(--primary)]",
      )}
      aria-expanded={expanded}
      aria-selected={expanded}
    >
      <td className="whitespace-nowrap py-[7px] pl-0 pr-2.5">
        <div className="flex items-center">
          <span className={cn("mr-2 h-[15px] w-[3px] shrink-0 rounded-sm", railClass)} aria-hidden />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-sans text-[12.5px] font-bold text-foreground">{row.ticker ?? "—"}</span>
              {assetTag(row) && (
                <span className="rounded-[3px] border border-border px-1 py-0 font-mono text-[8.5px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                  {assetTag(row)}
                </span>
              )}
              {isShadowEntry(top) && (
                <span className="rounded-[3px] border border-border bg-muted-foreground/15 px-1 py-0 font-mono text-[8.5px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                  Shadow
                </span>
              )}
              {row.discovery_lane === "ENERGY" && row.energy_score_snapshot && (
                <EnergyBadge snapshot={row.energy_score_snapshot} />
              )}
              {isStalenessOffender && <StalenessBadge generatedAt={ownGeneratedAt} />}
              {isInconsistent && <InconsistentExpressionBadge />}
            </div>
            <div className="mt-px max-w-[255px] truncate font-mono text-[10px] text-muted-foreground">
              <span className={cn("font-semibold", directionClass)} aria-hidden>
                {direction.kind === "BULLISH" ? "▲" : direction.kind === "BEARISH" ? "▼" : "◆"}
              </span>{" "}
              <span className="sr-only">{direction.label} · </span>
              {structureLine}
            </div>
          </div>
        </div>
      </td>
      <td className={cn(cell, "text-right")}><SignalMeter score={signal} /></td>
      <td className={cell}>
        <span className={cn("text-[11px]", VOL_REGIME_TONE_CLASS[volRegimeTone(row.iv_rv_regime)])}>{regime.label}</span>
      </td>
      <td className={cn(cell, "text-right", entryLabel ? "text-foreground" : "text-muted-foreground")}>
        {entryLabel ? (
          <>
            {side === "sell premium" && <span className="mr-1 text-muted-foreground">cr</span>}
            <span>{midLabel ?? "—"}</span>
            <span className="mx-1 text-muted-foreground">·</span>
            <span title={midLabel ? `Mid: ${midLabel}` : undefined}>{fillLabel ?? "—"}</span>
          </>
        ) : "—"}
      </td>
      <td className={cn(cell, "text-right", LIQUIDITY_TONE_CLASS[spreadSig?.tone ?? "neutral"])}>
        {spreadPctLabel ? (
          <span aria-label={spreadSig?.label ? `${spreadPctLabel}, ${spreadSig.label}` : spreadPctLabel}>
            {spreadPctLabel}
            {spreadSig?.label && <span className="ml-1 font-sans text-[9px] font-semibold normal-case tracking-[0.01em]">{spreadSig.label}</span>}
          </span>
        ) : "—"}
      </td>
      <td className={cn(cell, "text-right", maxLossLabel ? "text-foreground" : "text-muted-foreground")}>
        {maxLossLabel ?? "—"}
      </td>
      <td className={cn(cell, "text-right", popLabel ? "text-foreground" : "text-muted-foreground")}>
        {popLabel ?? "—"}
      </td>
      <td className={cn(cell, "text-right", beAdrView ? BE_ADR_TONE_CLASS[beAdrView.tone] : "text-muted-foreground")}>
        {beAdrView && beAdrLabel ? (
          <span aria-label={[beAdrLabel, beAdrView.word, beAdrView.qualifier].filter(Boolean).join(", ")}>
            {beAdrLabel}
            {beAdrView.word && <span className="ml-1 font-sans text-[9px] font-semibold normal-case tracking-[0.01em]">{beAdrView.word}</span>}
            {beAdrView.qualifier && <span className="ml-1 font-sans text-[9px] normal-case text-muted-foreground">({beAdrView.qualifier})</span>}
          </span>
        ) : "—"}
      </td>
      <td className={cell}>
        {withinVeto ? (
          <span className="text-[11px] font-semibold text-warning">E-{typeof daysToEarnings === "number" ? daysToEarnings : "soon"}</span>
        ) : nextEarnings ? <span className="text-[11px] text-muted-foreground">{nextEarnings}</span> : <span className="text-faint">—</span>}
      </td>
      <td className={cell}>
        <div className="flex items-center gap-2"><CompactOverlayFlag prefix="RS" view={rs} /><CompactOverlayFlag prefix="Flow" view={flow} /></div>
      </td>
      <td className={cn(cell, "text-right")}>
        <ChevronRight className={cn("ml-auto size-3 text-muted-foreground transition-transform", expanded && "rotate-90 text-foreground")} aria-hidden />
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Table section (eligible vs shadow bands)
// ---------------------------------------------------------------------------

type SortKey = "signal" | "ticker";
type SortDir = "asc" | "desc";

function HeaderCell({
  children,
  sortable,
  active,
  direction,
  onSort,
  className,
}: {
  children: ReactNode;
  sortable?: boolean;
  active?: boolean;
  direction?: SortDir;
  onSort?: () => void;
  className?: string;
}) {
  const base = "sticky top-0 z-10 whitespace-nowrap border-b border-border bg-background px-2.5 py-[7px] text-left align-middle font-mono text-[9px] font-semibold uppercase tracking-[0.09em] text-muted-foreground";
  if (!sortable) {
    return <th className={cn(base, className)}>{children}</th>;
  }
  return (
    <th className={cn(base, className)}>
      <button
        type="button"
        onClick={onSort}
        className={cn("inline-flex items-center gap-1 hover:text-foreground", active && "text-foreground")}
      >
        {children}
        <SortCaret active={Boolean(active)} direction={direction ?? "desc"} />
      </button>
    </th>
  );
}

function ScreenBand({
  rows,
  shadowRows = [],
  sortKey,
  sortDir,
  onSort,
  expandedId,
  onToggle,
}: {
  rows: Array<{ row: MultiStrategyScreenRow; top: MultiStrategyScreenEntry | undefined; id: string }>;
  shadowRows?: Array<{ row: MultiStrategyScreenRow; top: MultiStrategyScreenEntry | undefined; id: string }>;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  expandedId: string | null;
  onToggle: (id: string) => void;
}) {
  return (
    <div className="relative w-full overflow-x-auto">
      <table className="w-full min-w-[1180px] caption-bottom border-collapse text-[12px]">
        <thead>
          <tr>
            <HeaderCell sortable active={sortKey === "ticker"} direction={sortDir} onSort={() => onSort("ticker")}>
              Idea
            </HeaderCell>
            <HeaderCell className="text-right" sortable active={sortKey === "signal"} direction={sortDir} onSort={() => onSort("signal")}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help underline decoration-dotted underline-offset-2">
                    Signal
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[240px]">
                  How strongly our signals agree with this setup across setup, confirmation, and risk — not a probability of profit.
                </TooltipContent>
              </Tooltip>
            </HeaderCell>
            <HeaderCell>Vol read</HeaderCell>
            <HeaderCell className="text-right">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help underline decoration-dotted underline-offset-2">
                    Entry mid · fill
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[240px]">
                  Quoted package mid and estimated executable fill per share.
                </TooltipContent>
              </Tooltip>
            </HeaderCell>
            <HeaderCell className="text-right">
              <Tooltip>
                <TooltipTrigger asChild><span className="cursor-help underline decoration-dotted underline-offset-2">Sprd%</span></TooltipTrigger>
                <TooltipContent className="max-w-[240px]">Round-trip spread as a percentage of premium — amber at 8%, red at 15%.</TooltipContent>
              </Tooltip>
            </HeaderCell>
            <HeaderCell className="text-right">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help underline decoration-dotted underline-offset-2">
                    Max loss
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[220px]">
                  Maximum loss per 1 contract if this idea is wrong.
                </TooltipContent>
              </Tooltip>
            </HeaderCell>
            <HeaderCell className="text-right">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={0}
                    role="note"
                    aria-label={`PoP: ${IMPLIED_PROBABILITY_TOOLTIP}`}
                    className="cursor-help underline decoration-dotted underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    PoP*
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[260px]">{IMPLIED_PROBABILITY_TOOLTIP}</TooltipContent>
              </Tooltip>
            </HeaderCell>
            <HeaderCell className="text-right">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help underline decoration-dotted underline-offset-2">
                    BE×ADR
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[280px]">
                  Breakeven distance as a multiple of the name's own 20-day average daily range. Directional idea: amber at 1.2×, red at 1.6× (further is worse). Credit spread: cushion, amber below 1.2×, red below 0.6× (closer is worse).
                </TooltipContent>
              </Tooltip>
            </HeaderCell>
            <HeaderCell>Event</HeaderCell>
            <HeaderCell>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help underline decoration-dotted underline-offset-2">
                    Ctx
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[220px]">
                  Relative strength (RS) and options flow — hover a flag for the full read.
                </TooltipContent>
              </Tooltip>
            </HeaderCell>
            <HeaderCell className="text-right"><span className="sr-only">Expand</span></HeaderCell>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ row, top, id }) => (
            <ScreenRow
              key={id}
              row={row}
              top={top}
              expanded={expandedId === id}
              onToggle={() => onToggle(id)}
            />
          ))}
          {shadowRows.length > 0 && (
            <tr>
              <td colSpan={11} className="border-y border-border bg-surface px-2.5 py-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                Shadow research — internal only, never customer-eligible
              </td>
            </tr>
          )}
          {shadowRows.map(({ row, top, id }) => (
            <ScreenRow
              key={id}
              row={row}
              top={top}
              expanded={expandedId === id}
              onToggle={() => onToggle(id)}
            />
          ))}
        </tbody>
      </table>
      <p className="px-5 py-2.5 font-mono text-[9.5px] text-muted-foreground">
        *PoP = market-implied at fill, not a forecast · amber ≥ threshold, red ≥ high threshold
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// JS-1076 (T1b) item 2: book strip -- net delta/vega/theta, greeks coverage,
// and concentration cluster flags over the curated rows already returned
// (services/apis/domains/trade_ideas/screener_reads.py's
// ScreenerReadService.list_screen -> compute_book_summary). Renders nothing
// when `book_summary` is absent (an older API build, or a `{}` fallback --
// see multiStrategyBookSummarySchema's `emptyObjectToUndefined` preprocess)
// rather than a fabricated all-zero strip. `greeks_coverage` is the server's
// own pre-formatted "N/M" string, rendered verbatim (single source of truth
// for the honest-coverage framing, matching EditionPage's BookStrip).
// ---------------------------------------------------------------------------

function bookSignedText(value: string | null | undefined): string {
  if (value == null) return "—";
  const parsed = parseDecimalString(value);
  if (parsed === null) return value;
  const sign = parsed > 0 ? "+" : "";
  return `${sign}${value}`;
}

// JS-1076 (T1b, adversarial-review fix) item 3: the macro-event masthead
// line. `macro_event_context` is MARKET-WIDE -- run_multi_strategy_screen.py
// fetches the FRED calendar ONCE per run and stamps the SAME snapshot on
// every row of the sweep -- so this derives ONE sweep-level value rather than
// a per-row read.
//
// Retro adversarial-review FIX 3: rows can span multiple sweeps (the
// screener list has a 48h TTL before a scheduled sweep prunes it), and a
// later sweep's row can carry a DIFFERENT (newer) `macro_event_context`
// snapshot than an earlier sweep's row still sitting in the same page. The
// original "first available context wins" rule could therefore surface a
// STALE snapshot sitting ahead of a fresher one in row order, or -- worse --
// prefer an older row's "available" context over a newer row's honest
// "unavailable" one, which is backwards: newer information should never lose
// to older information just because it says "no event nearby" instead of
// naming one.
//
// The honest fix: consider every row whose `macro_event_context.as_of`
// actually parses (available or not), pick the NEWEST by `as_of`, and only
// use it if that pick is itself fresh (within 24h of now). If the newest
// parseable snapshot says unavailable, render nothing -- never fall back to
// an older "available" one. This is a client-side stopgap; the proper fix is
// a single cohort-level macro context computed once per read on the backend
// rather than inferred from whichever rows happen to share a page.
const MACRO_CONTEXT_FRESHNESS_MS = 24 * 60 * 60 * 1000;

function sweepMacroEventContext(
  rows: MultiStrategyScreenRow[],
  now: Date = new Date(),
): MacroEventContext | null {
  let newest: MacroEventContext | null = null;
  let newestMs: number | null = null;
  for (const row of rows) {
    const context = row.macro_event_context;
    if (!context) continue;
    const asOfMs = parseGeneratedAt(context.as_of);
    if (asOfMs === null) continue;
    if (newestMs === null || asOfMs > newestMs) {
      newest = context;
      newestMs = asOfMs;
    }
  }
  if (newest === null || newestMs === null) return null;
  if (now.getTime() - newestMs > MACRO_CONTEXT_FRESHNESS_MS) return null;
  return newest.available === true ? newest : null;
}

// JS-1096 (wireframe parity): restyled from a standalone paragraph into a
// masthead BLOCK (label + mono value line) so it composes into the single
// horizontal desk-terminal band DeskMasthead lays out below, matching the
// hi-fi prototype's `.masthead-block` treatment (desk-hifi/prototype.html).
// Presentation only -- `macroEventMastheadLine` (the honest-absence-aware
// text derivation) is untouched, still returns null exactly as before.
function MacroEventMasthead({ context }: { context: MacroEventContext | null }) {
  const line = macroEventMastheadLine(context);
  if (!line) return null;
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">Macro</span>
      <span className="truncate font-mono text-[11.5px] text-foreground">{line}</span>
    </div>
  );
}

// JS-1068: the market-wide V4.2 trend-regime snapshot rides the SAME
// per-row, sweep-wide provenance seam as `macro_event_context` above
// (`market_regime_at_publication` -- one snapshot stamped on every row of a
// sweep). "Newest snapshot wins" is the SAME derivation as
// `sweepMacroEventContext` (rows can span multiple sweeps under the 48h
// TTL), but freshness is judged DIFFERENTLY -- see the adversarial-review
// FIX 3 correction below.
//
// ADVERSARIAL-REVIEW FIX 3 (round 3, HIGH) correction: `snapshot.as_of` is a
// plain SESSION DATE (e.g. "2026-01-05", from Python's `date.isoformat()`),
// never a wall-clock timestamp. A prior cut of this function ran it through
// `parseGeneratedAt` (which parses it as midnight UTC on that calendar day)
// and compared the result against `MACRO_CONTEXT_FRESHNESS_MS` (24h) the
// SAME way it judges `macro_event_context`'s genuine wall-clock
// timestamps -- but a market-regime snapshot is a DAILY-CLOSE fact honestly
// dated to the LATEST COMPLETED session (see
// `run_multi_strategy_screen._resolve_live_market_regime`'s coverage-date
// contract), which for an ordinary Tuesday-afternoon sweep is MONDAY's
// close. Midnight-UTC-Monday to Tuesday-afternoon-now is already 40+ hours,
// and a Monday-morning sweep reporting FRIDAY's close is 60-90+ hours --
// both comfortably exceed a 24h TTL despite being the CORRECT, current
// reading. Every legitimate snapshot was therefore hidden essentially all
// the time.
//
// The fix: never apply timestamp-freshness math to a session-date field.
// Render the snapshot when its `as_of` equals the LATEST ELIGIBLE COMPLETED
// session -- but the frontend must not re-derive an exchange calendar
// (holidays, weekends, early closes) itself, so it relies on the BACKEND's
// own already-computed answer instead: `market_regime_expected_session`
// (see run_multi_strategy_screen.py's own docstring), stamped onto the SAME
// row as a tolerant, additive field. When that field is present, the check
// is an exact date-to-date match. When it is ABSENT (an older row/build
// that predates this field), the honest fallback is a LENIENT bound -- `
// as_of` within 5 CALENDAR days of `now` -- generous enough to cover a
// weekend plus a holiday (documented, deliberately looser than a real
// calendar check) without ever re-implementing exchange-calendar logic
// client-side.
const MARKET_REGIME_LENIENT_FALLBACK_DAYS = 5;

function sweepMarketRegime(
  rows: MultiStrategyScreenRow[],
  now: Date = new Date(),
): MarketRegimeSnapshot | null {
  let newest: MarketRegimeSnapshot | null = null;
  let newestAsOfMs: number | null = null;
  let expectedSession: string | null = null;
  for (const row of rows) {
    const snapshot = row.market_regime_at_publication;
    if (!snapshot) continue;
    const asOfMs = parseGeneratedAt(snapshot.as_of);
    if (asOfMs === null) continue;
    if (newestAsOfMs === null || asOfMs > newestAsOfMs) {
      newest = snapshot;
      newestAsOfMs = asOfMs;
      expectedSession = row.market_regime_expected_session ?? null;
    }
  }
  if (newest === null || newestAsOfMs === null) return null;

  if (expectedSession) {
    return newest.as_of === expectedSession ? newest : null;
  }
  // Lenient fallback: no expected-session anchor on this row (an older
  // build, or the backend resolver itself couldn't resolve a session this
  // run) -- accept an as_of within a generous multi-day window rather than
  // either hiding every legitimate snapshot (the old timestamp-TTL bug) or
  // rendering an arbitrarily stale one forever.
  const ageMs = now.getTime() - newestAsOfMs;
  const fallbackWindowMs = MARKET_REGIME_LENIENT_FALLBACK_DAYS * 24 * 60 * 60 * 1000;
  return ageMs <= fallbackWindowMs ? newest : null;
}

// JS-1096 (wireframe parity): restyled into a masthead block -- see
// MacroEventMasthead's comment immediately above for the same rationale.
function MarketRegimeMasthead({ snapshot }: { snapshot: MarketRegimeSnapshot | null }) {
  const line = marketRegimeMastheadLine(snapshot);
  if (!line) return null;
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">Market regime</span>
      <span className="truncate font-mono text-[11.5px] text-foreground">{line}</span>
    </div>
  );
}

// JS-1096 (wireframe parity): restyled from a standalone bordered card into
// a masthead block matching the prototype's "Book if taken" block (label +
// one mono value line, cluster-flag badges trailing inline) -- same facts,
// same honest-absence behavior (renders nothing when `book` is absent),
// composed into DeskMasthead's single horizontal band instead of sitting in
// its own bordered section below the legend.
function ScreenerBookStrip({ book }: { book: MultiStrategyBookSummary | null | undefined }) {
  if (!book) return null;
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">Book if taken (1× each)</span>
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11.5px] text-foreground">
        <span>
          <span className="text-muted-foreground">Net delta</span>{" "}
          <span className="tabular-nums">{bookSignedText(book.net_delta_shares)}</span>
        </span>
        <span>
          <span className="text-muted-foreground">Net vega</span>{" "}
          <span className="tabular-nums">{bookSignedText(book.net_vega_dollars)}</span>
        </span>
        <span>
          <span className="text-muted-foreground">Net theta/day</span>{" "}
          <span className="tabular-nums">{bookSignedText(book.net_theta_dollars_per_day)}</span>
        </span>
        <span>
          <span className="text-muted-foreground">Greeks coverage</span>{" "}
          <span className="tabular-nums">{book.greeks_coverage}</span>
        </span>
        {book.cluster_flags.length > 0 && (
          // Masthead density: one badge, not one per cluster — nine stacked
          // amber badges drowned the band. The full list stays reachable via
          // the title tooltip, so nothing is hidden, only summarized.
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge
              variant="outline"
              className="border-warning/30 text-warning"
              title={book.cluster_flags.map((flag) => flag.label).join(" · ")}
            >
              {book.cluster_flags[0].label}
              {book.cluster_flags.length > 1 ? ` · +${book.cluster_flags.length - 1} more` : ""}
            </Badge>
          </span>
        )}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// JS-1096 (wireframe parity): the desk masthead band -- composes the
// EXISTING MacroEventMasthead / MarketRegimeMasthead / ScreenerBookStrip
// (data hooks untouched, only their own inner markup restyled above) plus a
// compact running-record block into ONE horizontal terminal band, matching
// desk-hifi/prototype.html's `.masthead` -- regime | book-if-taken | macro
// line | running record + scoreboard link, left-to-right with hairline
// dividers. Every child keeps its own honest-absence behavior (renders
// nothing when its own data is absent); this wrapper renders nothing itself
// only when EVERY child would be empty, so a Saturday with no macro line and
// no book summary still shows whatever the running record has to say.
//
// JS-1096 round 2 (Codex review fix -- MED finding 3): the running record is
// now a genuinely COMPACT, right-aligned inline block in this SAME band
// (matching the prototype's `.masthead-record` layout), not the full
// EditionPage card in a second row under a divider. It shares its
// data/formatting with EditionPage.tsx's ScoreboardStrip via the
// `runningRecordFacts` primitive (screenerLabels.ts) -- a genuine
// extraction, not a fork: both surfaces derive the SAME won/lost/expired
// counts from the SAME function, they just lay them out differently (a
// small compact line here vs. a full card there).
// ---------------------------------------------------------------------------

function MastheadDivider() {
  return <span aria-hidden data-testid="masthead-divider" className="hidden min-h-7 self-stretch border-l border-border sm:block" />;
}

// JS-1096 round 2 (Codex review fix -- MED finding 1): `/editions/today`
// intentionally returns the latest PRIOR edition when today's has not been
// generated yet (edition.py's EditionReadService.get_today) -- so a running
// record rendered here with no date attached could present a stale count as
// current. The record itself stays (it is cumulative, so showing it is
// honest); every render path through this component instead carries the
// `editionLabel` provenance ("Edition #N · <date>") so a reader always knows
// which edition's snapshot they're looking at. This component NEVER renders
// the counts without that label -- `runningRecordFacts` guarantees the label
// is present whenever it returns a non-null result at all.
function RunningRecordCompact({ facts }: { facts: RunningRecordFacts }) {
  return (
    <div className="flex min-w-0 flex-col items-end gap-0.5 text-right">
      <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
        Running record · {facts.editionLabel}
      </span>
      <span className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1 font-mono text-[11.5px] text-foreground">
        {facts.total === 0 ? (
          <span className="text-muted-foreground">No resolved ideas yet</span>
        ) : (
          <>
            <span>
              <span className="tabular-nums text-gain">{facts.won}</span>W
            </span>
            <span>
              <span className="tabular-nums text-loss">{facts.lost}</span>L
            </span>
            {/* "expired" is the SAME outcome-kind word ScoreboardStrip uses
                (never abbreviated to "BE"/breakeven -- outcome_resolution.py's
                EXPIRED means "no rule ever triggered before expiry; settle
                at intrinsic value," a distinct concept from breakeven). */}
            <span>
              <span className="tabular-nums text-foreground">{facts.expired}</span> expired
            </span>
          </>
        )}
        <span aria-hidden className="text-muted-foreground">
          ·
        </span>
        <Link to="/portal/trade-ideas/edition" className="text-primary underline decoration-dotted underline-offset-2">
          scoreboard
        </Link>
      </span>
    </div>
  );
}

function DeskMasthead({
  api,
  macroEventContext,
  marketRegime,
  book,
}: {
  api: ApiFn;
  macroEventContext: MacroEventContext | null;
  marketRegime: MarketRegimeSnapshot | null;
  book: MultiStrategyBookSummary | null | undefined;
}) {
  // The running record lives on the daily edition, behind the SAME internal
  // allowlist boundary as the screener itself (both 403 for a non-internal
  // caller) -- reusing that hook here, rather than inventing a
  // screener-local equivalent, is the same "one data path, one derivation"
  // reuse this whole masthead is built from. A caller without edition
  // access (or no edition published/reachable at all) simply renders no
  // running-record block, below -- `runningRecordFacts` returns null for a
  // failed/absent query, never a fabricated zero record.
  const edition = useTradeIdeaEditionToday(api);
  const recordFacts = edition.isSuccess ? runningRecordFacts(edition.data) : null;

  const regimeLine = marketRegimeMastheadLine(marketRegime);
  const macroLine = macroEventMastheadLine(macroEventContext);
  const centerBlocks = [
    marketRegime && regimeLine != null ? <MarketRegimeMasthead key="regime" snapshot={marketRegime} /> : null,
    book ? <ScreenerBookStrip key="book" book={book} /> : null,
  ].filter((block): block is NonNullable<typeof block> => block != null);
  const hasRight = (macroEventContext && macroLine != null) || recordFacts;

  return (
    <div data-testid="desk-masthead" className="border-b border-border bg-surface px-5 py-2.5">
      <div className="flex flex-wrap items-stretch gap-x-5 gap-y-2">
        <div data-testid="masthead-identity" className="flex shrink-0 flex-col gap-0.5">
          <h2 className="text-[15px] font-bold tracking-[-0.01em] text-foreground">
            {recordFacts?.editionLabel ?? "Best Trade Ideas"}
          </h2>
          <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-warning">
            Internal · Shadow research
          </span>
        </div>

        {centerBlocks.length > 0 && <MastheadDivider />}
        <div data-testid="masthead-center" className="flex min-w-0 flex-1 flex-wrap items-stretch gap-x-5 gap-y-2">
          {centerBlocks.map((block, index) => (
            <Fragment key={block.key}>
              {index > 0 && <MastheadDivider />}
              {block}
            </Fragment>
          ))}
        </div>

        {hasRight && <MastheadDivider />}
        <div data-testid="masthead-right" className="ml-auto flex flex-wrap items-stretch justify-end gap-x-5 gap-y-2 text-right">
          {macroEventContext && macroLine != null && <MacroEventMasthead context={macroEventContext} />}
          {macroEventContext && macroLine != null && recordFacts && <MastheadDivider />}
          {recordFacts && (
            <div className="ml-auto">
              <RunningRecordCompact facts={recordFacts} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

const DRAWER_COLLAPSED_STORAGE_KEY = "desk.drawer.collapsed";

function readDrawerCollapsedPreference(): boolean {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(DRAWER_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeDrawerCollapsedPreference(collapsed: boolean): void {
  try {
    window.localStorage.setItem(DRAWER_COLLAPSED_STORAGE_KEY, String(collapsed));
  } catch {
    // Storage may be blocked or unavailable; the in-memory preference still works.
  }
}

export function MultiStrategyScreenTable({ api }: { api: ApiFn }) {
  // Default to the CURATED view (best ideas that cleared the quality bar). The
  // "Show all" toggle flips `curated` off, which the hook threads to the API as
  // a query param and refetches (the flag is part of the query key).
  const [curated, setCurated] = useState(true);
  const query = useMultiStrategyScreen(api, true, curated);
  const rows = useMemo(() => query.data?.items ?? [], [query.data]);

  const [sortKey, setSortKey] = useState<SortKey>("signal");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [drawerCollapsed, setDrawerCollapsed] = useState(readDrawerCollapsedPreference);

  useEffect(() => {
    writeDrawerCollapsedPreference(drawerCollapsed);
  }, [drawerCollapsed]);

  // JS-1082: "back to idea" arrives here as `?openRowId=<row_id>` (see
  // tickerContextLink.ts's buildBackToIdeaHref) -- once the row is actually
  // loaded, auto-expand its drawer so the visitor lands exactly where they
  // left off. Only ever expands (never collapses an idea the visitor already
  // opened by hand) and only fires once per row id -- a later manual toggle
  // of the SAME row must not be fought by this effect re-firing on an
  // unrelated re-render.
  const [searchParams] = useSearchParams();
  const requestedOpenRowId = searchParams.get("openRowId");
  useEffect(() => {
    if (!requestedOpenRowId) return;
    if (!rows.some((row) => row.row_id === requestedOpenRowId)) return;
    setExpandedId(requestedOpenRowId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires once per
    // (requestedOpenRowId, rows-loaded) pair; deliberately excludes
    // `expandedId` so a later manual collapse of this same row is never
    // silently re-forced back open by this effect.
  }, [requestedOpenRowId, rows]);

  const newestGeneratedAtDate = useMemo(() => newestGeneratedAt(rows), [rows]);
  const updatedLabel = useMemo(() => relativeUpdated(newestGeneratedAtDate), [newestGeneratedAtDate]);
  // JS-1086 review fix (HIGH): the summary badge reflects the WORST
  // staleness tier across every row (never just the newest row's), so a
  // stale or unknown-age straggler is never concealed behind a calm summary.
  const stalenessSummary = useMemo(
    () => summarizeStaleness(rows.map(rowGeneratedAt)),
    [rows],
  );
  const macroEventContext = useMemo(() => sweepMacroEventContext(rows), [rows]);
  const marketRegime = useMemo(() => sweepMarketRegime(rows), [rows]);

  const { eligible, shadow } = useMemo(() => {
    const decorated = rows.map((row, index) => {
      const top = topEntry(row);
      return {
        row,
        top,
        id: row.row_id ?? `${row.ticker ?? "row"}-${index}`,
        shadow: isShadowEntry(top),
      };
    });

    const compare = (
      a: { row: MultiStrategyScreenRow; top: MultiStrategyScreenEntry | undefined },
      b: { row: MultiStrategyScreenRow; top: MultiStrategyScreenEntry | undefined },
    ) => {
      let result: number;
      if (sortKey === "ticker") {
        result = (a.row.ticker ?? "").localeCompare(b.row.ticker ?? "");
      } else {
        const ca = signalUnit(a.row);
        const cb = signalUnit(b.row);
        const na = Number.isNaN(ca);
        const nb = Number.isNaN(cb);
        // Rows without a signal score always sink to the bottom regardless
        // of sort direction (mirrors the server's missing-field-last sort).
        if (na && nb) result = 0;
        else if (na) return 1;
        else if (nb) return -1;
        else result = ca - cb;
      }
      return sortDir === "desc" ? -result : result;
    };

    const sorted = [...decorated].sort(compare);
    return {
      eligible: sorted.filter((item) => !item.shadow),
      shadow: sorted.filter((item) => item.shadow),
    };
  }, [rows, sortKey, sortDir]);

  const onSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir(key === "signal" ? "desc" : "asc");
    }
  };

  const onToggle = (id: string) => {
    if (drawerCollapsed) {
      setDrawerCollapsed(false);
      setExpandedId(id);
      return;
    }
    setExpandedId((prev) => (prev === id ? null : id));
  };
  const selectedItem = expandedId
    ? [...eligible, ...shadow].find((item) => item.id === expandedId) ?? null
    : null;

  return (
    <section className="min-w-0 overflow-hidden border border-border bg-background">
      {!query.isPending && !query.isError ? (
        <DeskMasthead
          api={api}
          macroEventContext={macroEventContext}
          marketRegime={marketRegime}
          book={query.data?.book_summary}
        />
      ) : (
        <div className="border-b border-border bg-surface px-5 py-2.5">
          <h2 className="text-[15px] font-bold tracking-[-0.01em] text-foreground">Best Trade Ideas</h2>
          <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-warning">
            Internal · Shadow research
          </span>
        </div>
      )}

      {/* JS-1149 (DESK-012): which desk rules judged the ideas below. Read off
          the rows themselves (each carries its own block in
          `desk008_gate_evidence`), never a separate call. The WHOLE list is
          passed because the curated read picks a generation per listing, so a
          page can span a rules activation and carry more than one identity. */}
      <DeskRulesStrip rows={rows} />

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border bg-background px-5 py-2">
        <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">View</span>
        <Button
          size="sm"
          variant="outline"
          className="h-7 rounded px-2.5 font-mono text-[10.5px] font-semibold uppercase"
          onClick={() => setCurated((prev) => !prev)}
          aria-pressed={!curated}
        >
          {curated ? "Best ideas" : "All screened"}
        </Button>
        <span className="h-4 border-l border-border" aria-hidden />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[9px] text-muted-foreground">
          <LegendDot className="bg-gain" label="Bullish / confirms" />
          <LegendDot className="bg-loss" label="Bearish" />
          <LegendDot className="bg-warning" label="Conflict / event risk" />
          <LegendDot className="bg-info" label="Shadow" />
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          {!query.isPending && !query.isError && (
            <span className="font-mono text-[10.5px] text-muted-foreground">
              <span className="font-semibold text-foreground">{rows.length}</span>{" "}
              {rows.length === 1 ? "idea" : "ideas"}
              {curated ? " (best)" : " (all)"}
            </span>
          )}
          {/* JS-1087 (fix 2): this table's OWN refresh affordance, driving
              ONLY the screener query it renders -- previously the page-level
              header refresh button sat directly above this table but drove
              the SETUP list query instead, a control that visually appeared
              to govern this surface but did not touch it at all. No control
              may govern a surface it doesn't affect; this table now owns its
              own refresh, exactly as the setups section owns its (see
              TradeIdeasPage.tsx). */}
          <Button
            variant="outline"
            size="icon"
            className="size-7 rounded"
            aria-label="Refresh best trade ideas"
            title={query.isFetching ? "Refreshing…" : undefined}
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw className={query.isFetching ? "animate-spin" : ""} />
          </Button>
          {updatedLabel && (
            <span className="font-mono text-[10.5px] text-muted-foreground">Updated {updatedLabel}</span>
          )}
          {/* JS-1086 review fix (HIGH): the summary badge reflects the WORST
              staleness tier across every row (summarizeStaleness), never
              just the newest row's -- see that function's own docstring for
              why "newest wins" was an honesty bug. Gated the same as
              `updatedLabel` (only once rows have actually loaded) so an
              empty/pending/error screen never shows a fabricated freshness
              chip where no row exists to be fresh or stale. */}
          {!query.isPending && !query.isError && (
            <StalenessSummaryBadge summary={stalenessSummary} />
          )}
        </div>
      </div>

      {/* JS-1094 (demo blocker FIX 6): `items_dropped_count` is the number
          of raw rows the contract's per-row-tolerant decode
          (multiStrategyScreenItemsWithDropsSchema, contracts.ts) could not
          parse -- surfaced as a small honest note, mirroring EditionPage's
          IdeasSection and LedgerPage's identical drop-count notes, never a
          silent drop and never a full-page error over one bad row. */}
      {!query.isPending && !query.isError && (query.data?.items_dropped_count ?? 0) > 0 && (
        <p className="border-b border-border px-5 py-2 text-[11px] text-muted-foreground">
          {query.data?.items_dropped_count} idea{query.data?.items_dropped_count === 1 ? "" : "s"} could not be displayed.
        </p>
      )}

      <div className="flex min-h-[560px] items-stretch">
        <div className="min-w-0 flex-1 overflow-hidden">
          {query.isPending && (
            <div className="space-y-2 p-4" role="status" aria-label="Loading screener">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="h-9 animate-pulse rounded-sm bg-hover" />
              ))}
              <p className="text-sm text-muted-foreground">Loading screener…</p>
            </div>
          )}
          {query.isError && <p className="p-4 text-sm text-destructive" role="alert">Could not load the screener.</p>}
          {!query.isPending && !query.isError && rows.length === 0 && (
        // Honest-empty: an empty CURATED list is a legitimate "nothing cleared
        // the bar" state, not an error. Uncurated-empty means no rows at all.
        <div className="m-4 border border-dashed border-border bg-surface/40 px-4 py-6 text-center">
          <p className="text-sm text-muted-foreground">
            {curated
              ? "No ideas cleared the quality bar right now — check back during market hours."
              : "No screener rows yet."}
          </p>
          {curated && (
            <Button
              size="sm"
              variant="ghost"
              className="mt-2"
              onClick={() => setCurated(false)}
            >
              Show all screened rows
            </Button>
          )}
        </div>
          )}

          {!query.isPending && !query.isError && rows.length > 0 && (
            <ScreenBand
              rows={eligible}
              shadowRows={shadow}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
              expandedId={expandedId}
              onToggle={onToggle}
            />
          )}
        </div>

        {!query.isPending && !query.isError && rows.length > 0 && (
          <aside
            className={cn(
              "flex shrink-0 flex-col border-l border-border bg-surface",
              drawerCollapsed ? "w-9" : "w-[420px] xl:w-[460px] 2xl:w-[520px]",
            )}
            aria-label="Idea evaluation"
          >
            {drawerCollapsed ? (
              <button
                type="button"
                onClick={() => setDrawerCollapsed(false)}
                aria-label="Expand idea evaluation"
                aria-expanded="false"
                title="Expand evaluation"
                className="flex h-full min-h-[200px] w-9 flex-col items-center gap-3 py-2 text-muted-foreground hover:bg-hover hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              >
                <ChevronLeft className="size-4 shrink-0" aria-hidden />
                <span className="font-mono text-[9px] font-semibold tracking-[0.16em] [writing-mode:vertical-rl] rotate-180">
                  EVALUATION
                </span>
              </button>
            ) : selectedItem?.top ? (
              <ThesisDrawer row={selectedItem.row} top={selectedItem.top} onCollapse={() => setDrawerCollapsed(true)} />
            ) : (
              <div className="flex h-full min-h-0 flex-col">
                <header className="flex shrink-0 items-center justify-between border-b border-border px-3 py-2">
                  <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Evaluation</span>
                  <CollapseEvaluationButton onCollapse={() => setDrawerCollapsed(true)} />
                </header>
                <div className="flex flex-1 items-center justify-center px-10 text-center text-[12px] text-muted-foreground">
                  Select a row to open its evaluation.
                </div>
              </div>
            )}
          </aside>
        )}
      </div>
    </section>
  );
}
